// 生態系治理 REST 客戶端(S37,IA 定案 specs/01 §2.4;admin#6)。
// 後端契約 = restful /v3/admin/ecosystem-requests* + /v3/admin/ecosystems*
// (restful#75 !187 + admin#6 read model,已在 restful main)。時間戳一律微秒(µs since epoch)。
//
// admin 面動作:創建/加入審核佇列(?type=create|join|all)、核准/駁回(僅 create;
// join 核准權在 owner org)、inventory 瀏覽、suspend,以及 admin#6 read model
// 三端點:直接成員清單(/{id}/members)、effective 遞移展開(/{id}/effective)、
// join 申請監督(ecosystem-requests?type=join)。皆唯讀讀模型,不繞授權。

import { apiFetch } from '../../lib/api';

export type EcosystemKind = 'platform' | 'org_default' | 'custom';
export type EcosystemStatus = 'pending_review' | 'active' | 'rejected' | 'suspended' | 'retired';

export interface Ecosystem {
  ecosystem_id: string;
  display_name?: string;
  owner_org_id?: string; // platform 級為空
  kind: EcosystemKind | string;
  status: EcosystemStatus | string;
  created_by?: string;
  created_at: number;
  updated_at: number;
  reviewed_by?: string;
  reviewed_at?: number;
  reason?: string;
}

export type EcoRequestType = 'create' | 'join';
export type EcoRequestStatus = 'pending' | 'approved' | 'rejected';

export interface EcosystemRequest {
  request_id: string;
  type: EcoRequestType | string;
  status: EcoRequestStatus | string;
  applicant_org_id: string;
  ecosystem_id?: string; // create: 新建的 ecosystem id
  applicant_ecosystem_id?: string; // join: 申請方哪個 ecosystem 加入
  target_ecosystem_id?: string; // join: 被加入的 ecosystem
  target_owner_org_id?: string; // join: 需核准的 owner org
  reason?: string;
  decided_by?: string;
  decision_reason?: string;
  case_id?: string;
  created_by?: string;
  created_at: number;
  decided_at?: number;
}

export interface AdminReasonCase {
  reason: string;
  case_id: string;
}

export interface ListEcosystemsParams {
  kind?: string;
  status?: string;
  owner_org?: string;
  limit?: number;
}

// ---- admin#6 read model 形狀(對齊 restful service DTO) ----

/** 一個直接掛在生態系底下的成員 org。 */
export interface EcosystemMemberOrg {
  org_id: string;
  org_name?: string; // best-effort 由 developer_orgs.name 解析
  status: string; // 成員列(membership row)狀態:active/invited/requested/removed…
  added_via?: string;
  added_at?: number;
}

/** 一個巢狀在另一個生態系底下的子生態系(容器「含」它 —— 成員圖的 down edge)。 */
export interface EcosystemSubMember {
  ecosystem_id: string;
  display_name?: string;
  kind?: string;
  status?: string; // 子生態系 row 狀態
  member_status: string; // 巢狀邊(membership row)狀態
  added_via?: string;
  added_at?: number;
}

/** GET /v3/admin/ecosystems/{id}/members 回應:生態系表頭 + 直接成員(依 kind 分)。 */
export interface EcosystemMembersView {
  ecosystem_id: string;
  display_name?: string;
  kind: string;
  status: string;
  owner_org_id?: string;
  member_orgs: EcosystemMemberOrg[];
  sub_ecosystems: EcosystemSubMember[];
}

/** effective 遞移展開中的一個可達生態系(含深度 + via-chain)。 */
export interface EffectiveEcosystemNode {
  ecosystem_id: string;
  kind: string;
  depth: number;
  via_chain: string[]; // 從 seed 到此生態系的 id 串(可解釋性)
}

/** effective 遞移展開中的一個可達成員 org(含帶它進來的生態系 + chain)。 */
export interface EffectiveOrgNode {
  org_id: string;
  via_ecosystem: string;
  via_chain: string[];
}

/** GET /v3/admin/ecosystems/{id}/effective 回應:從此生態系視角的遞移閉包。 */
export interface EcosystemEffective {
  ecosystem_id: string;
  ecosystems: EffectiveEcosystemNode[];
  orgs: EffectiveOrgNode[];
  truncated?: boolean; // 有鏈觸及深度上限(3)未再展開
}

/** GET /v3/admin/ecosystem-requests?type=&status= — 審核佇列。
 * type 省略=create(沿用舊行為);'join'=加入申請監督;'all'=兩者。
 * status 省略=pending;'all'=全部歷史;approved/rejected=審核歷史分頁。 */
export async function listEcosystemRequests(status?: string, type?: string): Promise<EcosystemRequest[]> {
  const res = await apiFetch<{ items?: EcosystemRequest[] }>('/v3/admin/ecosystem-requests', { query: { status, type } });
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

/** POST /v3/admin/ecosystem-requests/{id}/approve — pending_review→active;reason+case_id 必填、audit。 */
export async function approveEcosystemRequest(requestId: string, audit: AdminReasonCase): Promise<EcosystemRequest> {
  const res = await apiFetch<EcosystemRequest>(
    `/v3/admin/ecosystem-requests/${encodeURIComponent(requestId)}/approve`,
    { method: 'POST', body: audit }
  );
  return res.data;
}

/** POST /v3/admin/ecosystem-requests/{id}/reject — 駁回;reason+case_id 必填、audit。 */
export async function rejectEcosystemRequest(requestId: string, audit: AdminReasonCase): Promise<EcosystemRequest> {
  const res = await apiFetch<EcosystemRequest>(
    `/v3/admin/ecosystem-requests/${encodeURIComponent(requestId)}/reject`,
    { method: 'POST', body: audit }
  );
  return res.data;
}

/** GET /v3/admin/ecosystems?kind=&status=&owner_org=&limit= — inventory 瀏覽(admin read)。 */
export async function listEcosystems(params: ListEcosystemsParams = {}): Promise<Ecosystem[]> {
  const res = await apiFetch<{ items?: Ecosystem[] }>('/v3/admin/ecosystems', {
    query: {
      kind: params.kind,
      status: params.status,
      owner_org: params.owner_org,
      limit: params.limit,
    },
  });
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

/** POST /v3/admin/ecosystems/{id}/suspend — reason+case_id 必填;kinio.ai(platform)→409 ECOSYSTEM_PROTECTED。 */
export async function suspendEcosystem(ecosystemId: string, audit: AdminReasonCase): Promise<Ecosystem> {
  const res = await apiFetch<Ecosystem>(
    `/v3/admin/ecosystems/${encodeURIComponent(ecosystemId)}/suspend`,
    { method: 'POST', body: audit }
  );
  return res.data;
}

/** GET /v3/admin/ecosystems/{id}/members — 直接成員(member orgs + 巢狀子生態系)。admin#6 唯讀 read model。
 * 成員列不論狀態全列(admin 也看得到 invited/removed);active-only 的互通閉包在 getEcosystemEffective。 */
export async function getEcosystemMembers(ecosystemId: string): Promise<EcosystemMembersView> {
  const res = await apiFetch<EcosystemMembersView>(`/v3/admin/ecosystems/${encodeURIComponent(ecosystemId)}/members`);
  const d = res.data;
  return {
    ...d,
    member_orgs: Array.isArray(d?.member_orgs) ? d.member_orgs : [],
    sub_ecosystems: Array.isArray(d?.sub_ecosystems) ? d.sub_ecosystems : [],
  };
}

/** GET /v3/admin/ecosystems/{id}/effective — 從此生態系視角的 effective 遞移展開
 * (via-chain + depth + truncated),重用 enforcement resolver 的 BFS。admin#6 唯讀。
 * 非 active 的種子生態系(suspended/pending)展開為空 —— 與 resolver 同義(不賦予互通)。 */
export async function getEcosystemEffective(ecosystemId: string): Promise<EcosystemEffective> {
  const res = await apiFetch<EcosystemEffective>(`/v3/admin/ecosystems/${encodeURIComponent(ecosystemId)}/effective`);
  const d = res.data;
  return {
    ...d,
    ecosystems: Array.isArray(d?.ecosystems) ? d.ecosystems : [],
    orgs: Array.isArray(d?.orgs) ? d.orgs : [],
  };
}
